<template>
	<div>
		<ListLayout
			:title="t('admin_overview_integrations_title')"
			:buttonText="t('admin_integrations_newintegration_label')"
			@click="onChangeTab({ id: 'AVAILABLE' })"
		>
			<template #search>
				<div></div>
			</template>

			<template #custom>
				<div class="tabs-wrapper">
					<Tabs
						class="integration-tabs"
						stretch
						:tabs="tabs"
						:defaultSelected="activeType"
						selectedcolor="#2c2d31"
						@change="onChangeTab"
					>
						<template v-slot="{ tab }">
							<div class="integration-tab" v-text="tab.text" />
						</template>
					</Tabs>
				</div>
			</template>

			<template #table>
				<Table
					v-if="activeType === 'CONNECTED'"
					:columns="columns"
					:rows="connections"
					:loadingOverlay="loading"
					infinite
					clickable
					loadMoreTrigger="0px"
					ref="table"
					height="calc(100vh - 143px)"
					hideHeader
					@row-click="showConnection"
					class="integrations-table"
				>
					<template #cell-row="{ data }">
						<div class="row">
							<div class="info">
								<div class="logo">
									<img
										:src="data.integration.icon_url"
										alt="integration icon"
									/>
								</div>
								<div>
									<div class="integration-title">
										{{ data.integration.name }}
									</div>
									<div class="account-info">{{ data.info }}</div>
								</div>
							</div>

							<div class="status">
								<CircleCheckIcon class="status-icon" type="regular" />
								<span>{{ data.status }}</span>
								<GearIcon class="gear-icon" />
							</div>
						</div>
					</template>

					<template #empty>
						<div class="empty-state">
							<img
								class="empty-state-icon"
								src="/images/common/integrations_empty.png"
								alt="Empty Integrations Icon"
							/>
							<div class="title" v-text="t('admin_integrations_empty_title')" />
							<Button link @click="onChangeTab({ id: 'AVAILABLE' })">{{
								t('admin_integrations_newintegration_label')
							}}</Button>
						</div>
					</template>
				</Table>

				<div v-if="activeType === 'AVAILABLE'" class="option-cards">
					<div
						v-for="option in integrations"
						:key="option.id"
						class="option-card"
						@click="showIntegration(option)"
					>
						<div class="option-img">
							<img :src="option.image_url" />
						</div>

						<div class="title-desc">
							<div>{{ option.name }}</div>
							<div>{{ option.desc }}</div>
						</div>
					</div>
				</div>
			</template>
		</ListLayout>
	</div>
</template>

<script setup>
import { ref, computed, onMounted, getCurrentInstance } from 'vue'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { Table, Button, Tabs, useAlert } from '@/components/common'
import { ListLayout, get } from '../common'
import { CircleCheckIcon, GearIcon } from '@/icons'

const types = {
	CONNECTED: 'CONNECTED',
	AVAILABLE: 'AVAILABLE',
}

// Reactive Variables
const activeType = ref(types.CONNECTED)
const loading = ref(false)
const loadingIntegrations = ref(false)
const connections = ref([])
const integrations = ref([])
const columns = ref([{ field: 'row' }])
const table = ref(null)

// Utilities
const router = useRouter()
const { t } = useI18n()
const alert = useAlert()

const tabs = computed(() => [
	{
		id: types.CONNECTED,
		text: t('admin_integrations_connected', {
			Count: connections.value.length,
		}),
	},
	{ id: types.AVAILABLE, text: t('admin_integrations_available') },
])

onMounted(async () => {
	await fetchConnections()
	await fetchIntegrations()
})

// Methods
function onChangeTab({ id }) {
	activeType.value = id
}

async function fetchConnections() {
	loading.value = true
	try {
		const response = await get('/organization/integrations/connections')
		connections.value = response.data
		loading.value = false
		table.value?.scrollToTop()
	} catch (e) {
		console.error(e)
		loading.value = false
		alert.error(t('common_api_error_save'))
	}
}

async function fetchIntegrations() {
	loadingIntegrations.value = true
	try {
		const response = await get('/organization/integrations')
		integrations.value = response.data
		loadingIntegrations.value = false
	} catch (e) {
		console.error(e)
		loadingIntegrations.value = false
		alert.error(t('common_api_error_save'))
	}
}

function showConnection({ id }) {
	router.push({ name: 'connection', params: { id } })
}

function showIntegration(option) {
	router.push({
		name: 'integration',
		params: { id: option.id },
		query: { integrationObject: JSON.stringify(option) },
	})
}
</script>

<style lang="scss" scoped>
@import 'src/sass/variables';

:deep(.list-layout) {
	header {
		height: 146px;
		border-bottom: solid 1px var(--neutral-200);
	}

	.empty-state {
		display: flex;
		align-items: center;
		flex-direction: column;
		.title {
			font-size: 14px;
			font-family: 'Avenir-Medium';
			color: var(--text-gray-medium);
			margin: 25px 0 15px 0;
		}
	}

	.row {
		display: flex;
		align-items: center;
		width: 100%;
		.info {
			display: flex;
			align-items: center;
			gap: 15px;

			.logo {
				height: 40px;
				width: 40px;
				border: solid 1px var(--neutral-200);
				padding: 2.5px;
				display: flex;
				align-items: center;
				justify-content: center;
				border-radius: 6.5px;
				img {
					border-radius: 6.5px;
				}
			}

			.integration-title {
				font-family: 'Avenir-Heavy';
				color: var(--neutral-800);
				font-size: 14px;
			}

			.account-info {
				font-size: 12px;
				color: var(--neutral-500);
			}
		}

		.status {
			display: flex;
			align-items: center;
			gap: 20px;
			.status-icon {
				color: var(--teal-300);
			}
			.gear-icon {
				font-size: 20px;
				color: var(--neutral-400);
				margin-left: 20px;
			}
		}
	}

	.option-cards {
		display: flex;
		padding: 25px;
		gap: 40px;
		cursor: pointer;
		background: var(--neutral-0);
		.option-card {
			width: 275px;
			.option-img {
				border: 0 solid transparent;
				border-top-right-radius: 12px;
				border-top-left-radius: 12px;
				display: flex;
				align-items: center;
				justify-content: center;
				height: 83px;
				overflow: hidden;
				width: 100%;
				img {
					width: 100%;
				}
			}

			.title-desc {
				border: 1px solid var(--neutral-200);
				border-top: none;
				border-bottom-right-radius: 12px;
				border-bottom-left-radius: 12px;
				padding: 12px;
				div:nth-child(1) {
					font-family: 'Avenir-Heavy';
					font-size: 14px;
					color: var(--neutral-800);
					margin-bottom: 8px;
				}
				div:nth-child(2) {
					font-family: 'Avenir-Medium';
					font-size: 12px;
					color: var(--neutral-500);
				}
			}
		}
	}
}

.tabs-wrapper {
	background-color: #f9f9f9;
	margin-left: -24px;
	margin-right: -24px;
	.tabs-container {
		border-bottom: none;
	}

	.integration-tabs {
		width: 400px;
	}
}

.empty-icon {
	font-size: 200px;
}
</style>
